/* eslint-disable max-lines */
import {
  addWeeks,
  differenceInDays,
  endOfDay,
  getHours,
  getMinutes,
  getWeek,
  lastDayOfWeek,
  startOfWeek,
  subWeeks,
  toDate
} from 'date-fns'
import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {ArrUtil, Time} from 'yougile-util'
import {
  DataObjectId,
  DeadlineStickerEntry,
  getColumnColorHex,
  getColumnColorNumber,
  Hub,
  isColorDark,
  isHub,
  TaskData
} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {MessageBox} from '@components/message-box'
import {getAllReportTasks} from '@components/statistics/stats-reports/stats-reports-get-tasks'
import {createUseStore} from '@containers/hooks'
import {Board} from '@extensions/board'
import {Column} from '@extensions/column'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {Sticker} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'
import {taskDataFromHub} from '@extensions/task-data-extractor'
import {minTaskHeight, roundedTo15Min} from './utils'

export interface TaskWithColor {
  task: TaskData
  isSubtask: boolean
  columnColor: string
  isColorDark: boolean
  marginShiftLeft: number
  marginShiftRight: number
  roundedStartDate: Date
  roundedDeadline: Date
}

interface CheckCol {
  checkAddTask: boolean
  col: Hub
}

export type Shift = 'prev' | 'next' | 'current'
export type ChangeAction = 'drag' | 'change-deadline'
export type TaskType =
  | 'with-start-date'
  | 'without-start-date'
  | 'all-day-single'
  | 'all-day-multiple'
  | 'all-day-with-time'
  | 'from-day-to-hour'
  | 'from-hour-to-day'
export type ScrollPosition =
  | 'right-corner'
  | 'left-corner'
  | 'middle-area'
  | 'none'

interface State {
  cursorGrabbing: boolean
  showAllDayWithStartDateTask: boolean
  scrollPosition: ScrollPosition | null
  newTaskTitle: string
  openedPopup: boolean
  startDatePopup: Date | null
  endDatePopup: Date | null
  initialStartDateOnDrag: Date | null
  initialEndDateOnDrag: Date | null
  currentDate: {
    prev: Date
    current: Date
    next: Date
  }
  weekNumber: {
    prev: number
    current: number
    next: number
  }
  tasksWithStartDate: {
    prev: TaskWithColor[]
    current: TaskWithColor[]
    next: TaskWithColor[]
  }
  tasksWithoutStartDate: {
    prev: TaskWithColor[]
    current: TaskWithColor[]
    next: TaskWithColor[]
  }
  tasksWithStartDateWithoutTime: {
    prev: TaskWithColor[]
    current: TaskWithColor[]
    next: TaskWithColor[]
  }
  tasksWithoutStartDateWithoutTime: {
    prev: TaskWithColor[]
    current: TaskWithColor[]
    next: TaskWithColor[]
  }
  currentTime: Date
  allDayTasksHeight: number
  showPopupButton: {
    value: boolean
    hour: Date | null
  }
  addTaskWithStartDate: boolean
  checkAddTask: boolean
  checkCols: CheckCol[]
  showMoreAllDayTasks: boolean
  openedAllDayPopup: boolean
  maxTasksInColHeight: number
}

const isTaskWithDeadlineOrStartDate = (t: Hub | TaskData): boolean => {
  const value = Sticker.getDeadline(t)
  return !!(value?.current?.deadline || value?.current?.startDate)
}

export class CalendarStore {
  state: State = {
    cursorGrabbing: false,
    showAllDayWithStartDateTask: false,
    scrollPosition: null,
    newTaskTitle: '',
    openedPopup: false,
    startDatePopup: null,
    endDatePopup: null,
    initialStartDateOnDrag: null,
    initialEndDateOnDrag: null,
    currentDate: {
      prev: subWeeks(Time.now(), 1),
      current: toDate(Time.now()),
      next: addWeeks(Time.now(), 1)
    },
    weekNumber: {
      prev: getWeek(subWeeks(Time.now(), 1)),
      current: getWeek(Time.now()),
      next: getWeek(addWeeks(Time.now(), 1))
    },
    tasksWithStartDate: {
      prev: [],
      current: [],
      next: []
    },
    tasksWithoutStartDate: {
      prev: [],
      current: [],
      next: []
    },
    tasksWithStartDateWithoutTime: {
      prev: [],
      current: [],
      next: []
    },
    tasksWithoutStartDateWithoutTime: {
      prev: [],
      current: [],
      next: []
    },
    currentTime: toDate(Time.now()),
    allDayTasksHeight: 1,
    showPopupButton: {
      value: false,
      hour: null
    },
    addTaskWithStartDate: true,
    checkAddTask: false,
    checkCols: [],
    showMoreAllDayTasks: false,
    openedAllDayPopup: false,
    maxTasksInColHeight: 0
  }

  constructor() {
    makeAutoObservable(this, {
      state: observable.shallow
    })
  }

  changeWeekHandle(btnType: Shift) {
    switch (btnType) {
      case 'prev': {
        const currentDatePrev = subWeeks(this.state.currentDate.prev, 1)
        const currentWeekPrev = getWeek(
          subWeeks(this.state.currentDate.prev, 1)
        )

        const currentDateToday = subWeeks(this.state.currentDate.current, 1)
        const currentWeekToday = getWeek(
          subWeeks(this.state.currentDate.current, 1)
        )

        const currentDateNext = subWeeks(this.state.currentDate.next, 1)
        const currentWeekNext = getWeek(
          subWeeks(this.state.currentDate.next, 1)
        )

        this.state.currentDate = {
          prev: currentDatePrev,
          current: currentDateToday,
          next: currentDateNext
        }
        this.state.weekNumber = {
          prev: currentWeekPrev,
          current: currentWeekToday,
          next: currentWeekNext
        }
        break
      }
      case 'current': {
        this.state.currentDate = {
          prev: subWeeks(Time.now(), 1),
          current: toDate(Time.now()),
          next: addWeeks(Time.now(), 1)
        }
        this.state.weekNumber = {
          prev: getWeek(subWeeks(Time.now(), 1)),
          current: getWeek(Time.now()),
          next: getWeek(addWeeks(Time.now(), 1))
        }
        break
      }
      case 'next': {
        const currentDatePrev = addWeeks(this.state.currentDate.prev, 1)
        const currentWeekPrev = getWeek(
          addWeeks(this.state.currentDate.prev, 1)
        )

        const currentDateToday = addWeeks(this.state.currentDate.current, 1)
        const currentWeekToday = getWeek(
          addWeeks(this.state.currentDate.current, 1)
        )

        const currentDateNext = addWeeks(this.state.currentDate.next, 1)
        const currentWeekNext = getWeek(
          addWeeks(this.state.currentDate.next, 1)
        )

        this.state.currentDate = {
          prev: currentDatePrev,
          current: currentDateToday,
          next: currentDateNext
        }
        this.state.weekNumber = {
          prev: currentWeekPrev,
          current: currentWeekToday,
          next: currentWeekNext
        }
        break
      }
    }
  }

  startDate(shift: Shift): Date {
    return startOfWeek(this.state.currentDate[shift], {weekStartsOn: 1})
  }

  endDate(shift: Shift): Date {
    return lastDayOfWeek(this.state.currentDate[shift], {weekStartsOn: 1})
  }

  get currentHourAndMinute(): [number, number] {
    return [
      getHours(this.state.currentTime),
      getMinutes(this.state.currentTime)
    ]
  }

  taskPackaging(
    t: Hub | TaskData,
    options: {
      shift: Shift
      startWeek: number
      endWeek: number
      isSubtask?: boolean
      isReport?: boolean
    }
  ): void {
    const {shift, startWeek, endWeek, isSubtask = false} = options
    const taskData = isHub(t) ? taskDataFromHub(Sync.getCurrentSandbox(), t) : t
    if (isTaskWithDeadlineOrStartDate(taskData)) {
      const deadline = Sticker.getDeadline(taskData).current.deadline
      const startDate = Sticker.getDeadline(taskData).current.startDate
      const withTime = Sticker.getDeadline(taskData).current.withTime
      if (
        Time.dayBetween(startDate, startWeek, endWeek) ||
        Time.dayBetween(deadline, startWeek, endWeek) ||
        differenceInDays(deadline, startDate) > 7
      ) {
        const color = getColumnColorNumber(
          Task.getById(Task.findContainingColumnId(t.id))
        )
        const roundedStartDate = roundedTo15Min(Time.toLocalDate(startDate))
        const roundedDeadline = roundedTo15Min(Time.toLocalDate(deadline))

        const task: TaskWithColor = {
          task: taskData,
          isSubtask,
          columnColor: getColumnColorHex(color, globals.theme, true),
          isColorDark: isColorDark(color),
          marginShiftLeft: 0,
          marginShiftRight: 0,
          roundedStartDate: startDate ? roundedStartDate : null,
          roundedDeadline:
            Time.toTimestamp(roundedDeadline) -
              Time.toTimestamp(roundedStartDate) <
            minTaskHeight * Time.minute
              ? new Date(
                  Time.toTimestamp(roundedStartDate) +
                    minTaskHeight * Time.minute
                )
              : roundedDeadline
        }
        if (withTime) {
          if (startDate) {
            this.state.tasksWithStartDate[shift].push(task)
          } else {
            this.state.tasksWithoutStartDate[shift].push(task)
          }
        } else {
          if (startDate) {
            this.state.tasksWithStartDateWithoutTime[shift].push({
              ...task,
              roundedStartDate: Time.toLocalDate(Time.middleOfDay(startDate)),
              roundedDeadline: Time.toLocalDate(Time.middleOfDay(deadline))
            })
          } else {
            this.state.tasksWithoutStartDateWithoutTime[shift].push({
              ...task,
              roundedDeadline: Time.toLocalDate(Time.middleOfDay(deadline))
            })
          }
        }
      }
    }
  }

  getTasksWithStartDate(shift: Shift): TaskWithColor[] {
    return ArrUtil.unique(this.state.tasksWithStartDate[shift], x => x.task.id)
  }

  getTasksWithoutStartDate(shift: Shift): TaskWithColor[] {
    return ArrUtil.unique(
      this.state.tasksWithoutStartDate[shift],
      x => x.task.id
    )
  }

  getTasksWithStartDateWithoutTime(shift: Shift): TaskWithColor[] {
    return ArrUtil.unique(
      this.state.tasksWithStartDateWithoutTime[shift],
      x => x.task.id
    )
  }

  getTasksWithoutStartDateWithoutTime(shift: Shift): TaskWithColor[] {
    return ArrUtil.unique(
      this.state.tasksWithoutStartDateWithoutTime[shift],
      x => x.task.id
    )
  }

  colPackaging(
    col: Hub,
    board: Hub,
    options: {
      shift: Shift
      startWeek: number
      endWeek: number
    }
  ): void {
    const {shift, startWeek, endWeek} = options
    Column.listTasks(col, true, {
      sorted: true,
      withoutArchive: true,
      board
    }).forEach(t => {
      const list = SubtaskUtil.getSubtaskList(t)
      const subtasks = SubtaskUtil.getSubtasksFromList(list, true)
      subtasks.forEach(subtask => {
        const taskHub = SubtaskUtil.convertToTask(subtask)
        this.taskPackaging(taskHub, {
          shift,
          startWeek,
          endWeek,
          isSubtask: true
        })
      })
      this.taskPackaging(t, {shift, startWeek, endWeek})
    })
  }

  calculateCurrentTasks(board: Hub, shift: Shift): void {
    this.state.tasksWithStartDate = {
      ...this.state.tasksWithStartDate,
      [shift]: []
    }

    this.state.tasksWithoutStartDate = {
      ...this.state.tasksWithoutStartDate,
      [shift]: []
    }

    this.state.tasksWithStartDateWithoutTime = {
      ...this.state.tasksWithStartDateWithoutTime,
      [shift]: []
    }

    this.state.tasksWithoutStartDateWithoutTime = {
      ...this.state.tasksWithoutStartDateWithoutTime,
      [shift]: []
    }

    this.state.checkCols = []

    if (board) {
      const startWeek = Time.toTimestamp(this.startDate(shift))
      const endWeek = Time.toTimestamp(endOfDay(this.endDate(shift)))
      const box = Sync.getCurrentSandbox()
      Board.listColumns(board, {sorted: true, noMirrors: false}).forEach(
        col => {
          if (Column.isMirror(col)) {
            if (col.data.report) {
              const reportTasks = getAllReportTasks(
                box,
                col.data.report,
                undefined,
                board
              )
              reportTasks.forEach(reportTask => {
                this.taskPackaging(reportTask, {
                  shift,
                  startWeek,
                  endWeek
                })
              })
            } else {
              Column.getMirroredColumns(col).forEach(mirroredCol => {
                this.colPackaging(mirroredCol, board, {
                  shift,
                  startWeek,
                  endWeek
                })
              })
            }
          } else {
            this.state.checkCols.push({
              checkAddTask: PermissionsChecker.checkAddChildTo(col),
              col
            })
            this.colPackaging(col, board, {
              shift,
              startWeek,
              endWeek
            })
          }
        }
      )
      this.state.checkAddTask = this.state.checkCols.some(el => el.checkAddTask)
    }
  }

  setTime(time: Date): void {
    this.state.currentTime = time
  }

  setOpenedPopup(val: boolean): void {
    this.state.openedPopup = val
  }

  setOpenedAllDayPopup(val: boolean): void {
    this.state.openedAllDayPopup = val
  }

  setNewTaskTitle(title: string): void {
    this.state.newTaskTitle = title
  }

  createNewTask(
    colId: DataObjectId,
    opts: {
      withTime: boolean
      assignedIds: DataObjectId[]
      startDate?: Date
      endDate: Date
    }
  ): void {
    if (!this.state.newTaskTitle.trim()) {
      return
    }
    const deadlineSticker: DeadlineStickerEntry = {
      deadline: Time.toTimestamp(opts.endDate),
      timestamp: Time.now(),
      by: globals.currentUserId,
      withTime: opts.withTime,
      notifyBefore: 0
    }

    const newTask = Task.createTask(this.state.newTaskTitle, {
      location: colId,
      assigned: opts.assignedIds
    })

    if (
      newTask &&
      PermissionsChecker.checkEditTaskStickers(newTask, 'not-user', undefined)
    ) {
      Task.pinDeadline(
        newTask,
        opts.startDate
          ? {
              ...deadlineSticker,
              startDate: Time.toTimestamp(opts.startDate)
            }
          : deadlineSticker
      )
      Task.pinFilters(newTask, 'create-via-calendar')
    } else {
      MessageBox.warn(Lang.msg.calendar.noRightToSetDeadline)
    }

    this.syncState(() => this.setOpenedPopup(false))
    this.state.newTaskTitle = ''
  }

  updateScrollPosition(scrollPosition: ScrollPosition): void {
    this.state.scrollPosition = scrollPosition
  }

  setEndDatePopup(endDate: Date): void {
    this.state.endDatePopup = endDate
  }

  setStartDatePopup(startDate: Date): void {
    this.state.startDatePopup = startDate
  }

  setInitialDragStartDate(startDate: Date): void {
    this.state.initialStartDateOnDrag = startDate
  }

  setInitialDragEndDate(endDate: Date): void {
    this.state.initialEndDateOnDrag = endDate
  }

  setShowAllDayWithStartDateTask(showAllDayWithStartDateTask: boolean): void {
    this.state.showAllDayWithStartDateTask = showAllDayWithStartDateTask
  }

  syncState(callback?: () => void): void {
    setTimeout(() => {
      runInAction(() => {
        this.calculateCurrentTasks(PageService.getCurrentBoard(), 'current')
        callback?.()
      })
    }, 50)
  }

  setCursorGrabbing(value: boolean): void {
    this.state.cursorGrabbing = value
  }

  setAllDayTasksHeight(value: number): void {
    this.state.allDayTasksHeight = value
  }

  showedPopupButton(value: boolean, hour: Date | null): void {
    this.state.showPopupButton = {
      value,
      hour
    }
  }

  setAddTaskWithStartDate(value: boolean): void {
    this.state.addTaskWithStartDate = value
  }

  clearDatePopup(): void {
    this.state.startDatePopup = null
    this.state.endDatePopup = null
    this.state.newTaskTitle = ''
  }

  toggleShowMoreAllDayTasks(): void {
    this.state.showMoreAllDayTasks = !this.state.showMoreAllDayTasks
  }

  setMaxTasksInColHeight(val: number): void {
    this.state.maxTasksInColHeight = val
  }
}

const [CalendarContext, useCalendarStore] = createUseStore(CalendarStore)
export {CalendarContext, useCalendarStore}
